import { isAnyOf, UnknownAction } from '@reduxjs/toolkit'
import { createReducer } from '@reduxjs/toolkit/react'
import { CaseWorkerRole, CaseWorkerStatus } from '@repo/shared/dtos/caseWorker'
import {
  CategorySettings,
  Language,
  LifeCycleStage,
  OnboardingStep,
} from '@repo/shared/dtos/organization'
import {
  StructuredDataDisplayDTO,
  StructuredDataSchemaDTO,
} from '@repo/shared/dtos/structured-data'
import { AsyncData, DataStatus } from '@repo/ui/utils'

import { adminStoreActions, RejectValue, storeActions, sudoStoreActions } from '@/store/actions'
import { FeatureFlagForOrganization } from '@/store/feature-flags/types'

import { RootState } from '.'

export type OrganizationChatSettings = {
  colors: {
    '--color-gradient-start': [number, number, number]
    '--color-gradient-end': [number, number, number]
    '--color-highlight': string
    '--color-primary-default': string
    '--color-primary-50': string
    '--color-primary-100': string
    '--color-primary-200': string
    '--color-primary-300': string
    '--color-primary-400': string
    '--color-primary-500': string
    '--color-primary-600': string
    '--color-primary-700': string
    '--color-primary-800': string
    '--color-primary-900': string
    '--color-primary-950': string
  }
  imageUrl: string
  allowFeedback?: boolean
}

export type OrganizationTexts = {
  beneficiary_app: {
    information_notice: {
      en: string
      fr: string
      es: string
      pt: string
    }
    welcome: {
      en: string
      fr: string
      es: string
      pt: string
    }
  }
}

export type CaseWorker = Omit<User, 'superAdmin'> & {
  role: CaseWorkerRole
  status: CaseWorkerStatus
}
export type Organization = {
  lifeCycleStage: LifeCycleStage
  categoriesSettings: CategorySettings[]
  chatAnswerGoal: string
  chatAssistantName: string
  chatMessageInstructions: string
  chatSafetyRules: string
  chatSettings?: OrganizationChatSettings
  commonSources: Record<string, { config: { subSources: string[] } }>
  description: string
  featureFlags: FeatureFlagForOrganization[]
  id: string
  language: Language
  methodology: string
  name: string
  onboarding: {
    step: OnboardingStep
  }
  texts?: OrganizationTexts
  url?: string
  structuredDataSchema: StructuredDataSchemaDTO
  structuredDataDisplay: StructuredDataDisplayDTO
  beneficiaryProfileTemplate?: string
}

export type User = {
  email: string
  firstName: string
  id: string
  language?: Language
  lastName: string
  superAdmin: boolean
}

export type UserState = {
  isLoggingIn: boolean
  me: AsyncData<User>
  organizations: AsyncData<Organization[]>
  caseWorker: AsyncData<CaseWorker>
  currentOrganizationId?: string
}

export const userInitialState: UserState = {
  isLoggingIn: false,
  me: DataStatus.Uninitialized,
  organizations: DataStatus.Uninitialized,
  caseWorker: DataStatus.Uninitialized,
}

export const userReducer: ((state: UserState | undefined, action: UnknownAction) => UserState) & {
  getInitialState: () => UserState
} = createReducer(userInitialState, (builder) => {
  builder
    .addCase(storeActions.initApp.pending, (state) => {
      if (!DataStatus.isFulfilled(state.me)) state.me = DataStatus.Loading
      if (!DataStatus.isFulfilled(state.organizations)) state.organizations = DataStatus.Loading
    })
    .addCase(storeActions.initApp.fulfilled, (state, { payload: { user } }) => {
      state.me = user
    })
    .addCase(storeActions.initApp.rejected, (state) => {
      state.me = DataStatus.Error
    })

  builder
    .addCase(storeActions.login.pending, (state) => {
      state.isLoggingIn = true
    })
    .addCase(storeActions.login.rejected, (state) => {
      state.isLoggingIn = false
    })
  builder.addCase(
    storeActions.initOrganization.pending,
    (
      state,
      {
        meta: {
          arg: { organizationId, isSameOrganization },
        },
      },
    ) => {
      if (isSameOrganization) return
      state.currentOrganizationId = organizationId
      state.caseWorker = DataStatus.Loading
    },
  )

  builder.addCase(
    storeActions.caseWorker.updateCurrent.fulfilled,
    (state, { payload: { caseWorker } }) => {
      if (!DataStatus.isFulfilled(state.caseWorker)) return
      if (caseWorker.id === state.caseWorker.id) {
        state.caseWorker = caseWorker
      }
    },
  )

  builder.addCase(storeActions.cleanUpOrganization, (state) => {
    state.currentOrganizationId = undefined
    state.caseWorker = DataStatus.Uninitialized
  })

  builder.addCase(sudoStoreActions.user.update.fulfilled, (state, { payload: { user } }) => {
    if (DataStatus.isFulfilled(state.me) && state.me.id === user.id) state.me = user
  })

  builder.addMatcher(
    isAnyOf(
      storeActions.initApp.fulfilled,
      sudoStoreActions.user.updateOrganizationsAccess.fulfilled,
    ),
    (state, { payload: { organizations } }) => {
      state.organizations = organizations
    },
  )

  builder
    .addMatcher(
      isAnyOf(
        storeActions.initOrganization.rejected,
        adminStoreActions.onboarding.createOrganization.rejected,
      ),
      (state) => {
        state.currentOrganizationId = undefined
        state.caseWorker = DataStatus.Error
      },
    )
    .addMatcher(
      isAnyOf(
        storeActions.initOrganization.fulfilled,
        adminStoreActions.onboarding.createOrganization.fulfilled,
      ),
      (state, { payload }) => {
        if (!DataStatus.isFulfilled(state.organizations)) return
        const { organization, caseWorker } = payload
        const found = state.organizations.findIndex((org) => org.id === organization.id)
        if (found === -1) {
          state.currentOrganizationId = undefined
          state.caseWorker = DataStatus.Uninitialized
          console.error(RejectValue.NoOrganization)
          return
        }

        state.caseWorker = caseWorker
      },
    )

  builder.addMatcher(
    isAnyOf(
      adminStoreActions.onboarding.createOrganization.fulfilled,
      storeActions.initOrganization.fulfilled,
      adminStoreActions.organization.update.fulfilled,
      adminStoreActions.onboarding.createOrganization.fulfilled,
      adminStoreActions.onboarding.setNextStep.fulfilled,
      adminStoreActions.organization.updateStructuredDataSchema.fulfilled,
      adminStoreActions.organization.updateStructuredDataDisplay.fulfilled,
    ),
    (state, { payload: { organization } }) => {
      updateOrganizations({ state, organization })
    },
  )
})

export function updateOrganizations({
  state,
  organization,
}: {
  state: UserState
  organization: Organization
}): Organization[] {
  if (DataStatus.isFulfilled(state.organizations) && state.organizations.length > 0) {
    // update
    state.organizations = state.organizations.map((org) =>
      org.id === organization.id ? organization : org,
    )
  } else {
    // Add
    state.organizations = [organization]
  }

  return state.organizations
}

export const getCurrentOrganization = (state: RootState) => {
  if (!DataStatus.isFulfilled(state.user.organizations)) return undefined
  if (!state.user.currentOrganizationId) return undefined
  return state.user.organizations.find((org) => org.id === state.user.currentOrganizationId)
}

export const getCurrentOrganizationStructuredDataSchema = (state: RootState) => {
  const organization = getCurrentOrganization(state)
  if (!organization) return undefined
  return organization.structuredDataSchema
}
